import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { saveFormResponses, supabase } from '../../services/supabaseClient';
import { FiSave, FiCheck, FiAlertCircle, FiArrowLeft, FiArrowRight } from 'react-icons/fi';

const FormBase = ({ 
  formType, 
  formTitle, 
  formDescription, 
  sections, 
  children 
}) => {
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {}
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const [savedData, setSavedData] = useState(null);
  
  // Verificar se há dados salvos anteriormente
  useEffect(() => {
    const loadSavedData = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('form_responses')
          .select('*')
          .eq('user_id', user.id)
          .eq('form_type', formType)
          .eq('status', 'draft')
          .order('created_at', { ascending: false })
          .limit(1);
          
        if (error) throw error;
        
        if (data && data.length > 0 && Object.keys(data[0].responses).length > 0) {
          setSavedData(data[0]);
          // Preencher o formulário com os dados salvos
          methods.reset(data[0].responses);
          setFormStatus({
            type: 'info',
            message: 'Formulário carregado com dados salvos anteriormente.'
          });
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error);
      }
    };
    
    loadSavedData();
  }, [user, formType, methods]);
  
  // Função para auto-salvar o formulário a cada 30 segundos
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      const formData = methods.getValues();
      
      // Only save if municipality is filled (for section 1) or if not section 1
      const shouldSave = activeSection !== 0 ||
                        (formData.nome_municipio && formData.nome_municipio.trim() !== '');
      
      if (shouldSave && Object.keys(formData).length > 0) {
        try {
          setAutoSaveStatus({ type: 'loading', message: 'Salvando...' });
          
          const { success, error } = await saveFormResponses(
            formType,
            formData,
            user.id,
            'draft'
          );
          
          if (success) {
            setAutoSaveStatus({ 
              type: 'success', 
              message: `Salvo automaticamente às ${new Date().toLocaleTimeString()}` 
            });
            
            setTimeout(() => {
              setAutoSaveStatus(null);
            }, 3000);
          } else {
            throw new Error(error);
          }
        } catch (error) {
          console.error('Erro ao salvar automaticamente:', error);
          setAutoSaveStatus({ 
            type: 'error', 
            message: 'Erro ao salvar. Tente novamente.' 
          });
        }
      }
    }, 120000); // A cada 2 minutos
    
    return () => clearInterval(autoSaveInterval);
  }, [user, formType, methods, activeSection]);
  
  const prefixFormData = (data) => {
  const prefixedMap = new Map();
  sections.forEach((section, idx) => {
    const sectionPrefix = `secao_${idx + 1}`;
    section.fields.forEach((field) => {
      prefixedMap.set(
        `${sectionPrefix}_${field}`,
        data[field] !== undefined ? data[field] : null
      );
    });
  });
  // Converte o Map ordenado em objeto simples
  return Object.fromEntries(prefixedMap.entries());
};

const onSubmit = async (data, event) => {
  setIsSubmitting(true);
  setFormStatus(null);
    
    try {
      // Não prefixar os campos na submissão, mantendo o mesmo formato do rascunho
      const formData = data;
      // console.log('[FormBase] Dados enviados na submissão:', JSON.stringify(formData, null, 2));
      const { success, error } = await saveFormResponses(
        formType,
        formData,
        user.id,
        'submitted'
      );
      
      if (success) {
        setFormStatus({
          type: 'success',
          message: 'Formulário enviado com sucesso!'
        });
        
        // Redirecionar após 2 segundos
        setTimeout(() => {
          navigate('/forms');
        }, 2000);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setFormStatus({
        type: 'error',
        message: 'Erro ao enviar o formulário. Tente novamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSaveDraft = async () => {
    setIsSubmitting(true);
    setFormStatus(null);
    
    try {
      const formData = methods.getValues();
      
      const { success, error } = await saveFormResponses(
        formType,
        formData,
        user.id,
        'draft'
      );
      
      if (success) {
        setFormStatus({
          type: 'success',
          message: 'Rascunho salvo com sucesso!'
        });
        
        // Limpar o status após 3 segundos
        setTimeout(() => {
          setFormStatus(null);
        }, 3000);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      setFormStatus({
        type: 'error',
        message: 'Erro ao salvar o rascunho. Tente novamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const nextSection = async () => {
    const currentSectionFields = sections[activeSection].fields || [];
    
    // Validar apenas os campos da seção atual
    const isValid = await methods.trigger(currentSectionFields);
    
    if (isValid) {
      setActiveSection(prev => Math.min(prev + 1, sections.length - 1));
      window.scrollTo(0, 0);
    }
  };
  
  const prevSection = () => {
    setActiveSection(prev => Math.max(prev - 1, 0));
    window.scrollTo(0, 0);
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{formTitle}</h1>
        <p className="mt-2 text-gray-600">{formDescription}</p>
      </div>
      
      {/* Progresso do formulário */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Seção {activeSection + 1} de {sections.length}
          </span>
          <span className="text-sm font-medium text-gray-700">
            {Math.round(((activeSection + 1) / sections.length) * 100)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${((activeSection + 1) / sections.length) * 100}%` }}
          ></div>
        </div>
      </div>
      
      {/* Status do formulário */}
      {formStatus && (
        <div className={`mb-6 p-4 rounded-md ${
          formStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          formStatus.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          <div className="flex items-start">
            {formStatus.type === 'success' && <FiCheck className="h-5 w-5 mr-2 mt-0.5" />}
            {formStatus.type === 'error' && <FiAlertCircle className="h-5 w-5 mr-2 mt-0.5" />}
            {formStatus.type === 'info' && <FiAlertCircle className="h-5 w-5 mr-2 mt-0.5" />}
            <span>{formStatus.message}</span>
          </div>
        </div>
      )}
      
      {/* Auto-save status */}
      {autoSaveStatus && (
        <div className={`mb-4 p-2 rounded-md text-sm ${
          autoSaveStatus.type === 'success' ? 'bg-green-50 text-green-700' :
          autoSaveStatus.type === 'error' ? 'bg-red-50 text-red-700' :
          'bg-gray-50 text-gray-700'
        }`}>
          <div className="flex items-center">
            {autoSaveStatus.type === 'loading' && (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600 mr-2"></div>
            )}
            {autoSaveStatus.type === 'success' && <FiCheck className="h-4 w-4 mr-2" />}
            {autoSaveStatus.type === 'error' && <FiAlertCircle className="h-4 w-4 mr-2" />}
            <span>{autoSaveStatus.message}</span>
          </div>
        </div>
      )}
      
      {/* Formulário */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {sections[activeSection].title}
          </h2>
          
          <FormProvider {...methods}>
            <form onSubmit={e => e.preventDefault()}>
              {/* Conteúdo da seção atual */}
              <div className="space-y-6">
                {React.Children.toArray(children)[activeSection]}
              </div>
              
              {/* Navegação entre seções */}
              <div className="mt-8 flex justify-between">
                <button
                  type="button"
                  onClick={prevSection}
                  disabled={activeSection === 0}
                  className={`btn inline-flex items-center ${
                    activeSection === 0 
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                      : 'btn-outline'
                  }`}
                >
                  <FiArrowLeft className="mh-4 w-4 mx-auto sm:mr-2" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>
                
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={isSubmitting}
                    className="btn inline-flex items-center btn-outline"
                  >
                    <FiSave className="h-4 w-4 mx-auto sm:mr-2" />
                    <span className="hidden sm:inline">Salvar Rascunho</span>
                  </button>
                  
                  {activeSection < sections.length - 1 ? (
                    <button
                      type="button"
                      onClick={nextSection}
                      className="btn inline-flex items-center btn-primary"
                    >
                      <span className="hidden sm:inline">Próximo</span>
                      <FiArrowRight className="h-4 w-4 mx-auto sm:mr-2" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="btn inline-flex items-center btn-primary"
                      onClick={() => methods.handleSubmit(onSubmit)()}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <FiCheck className="h-4 w-4 mx-auto sm:mr-2" />
                          <span className="hidden sm:inline">Finalizar e Enviar</span>
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default FormBase;
